// RightInspector — P1 primitive per 02_P1_RightInspector_v4_Plan.md.
//
// 3 states: expanded (304px) → rail (48px) → hidden → expanded.
// Cookie `shell.rightInspector`, SameSite=Lax, default=hidden.
// Independent provider (NOT coupled to SidebarProvider).
// Desktop: <aside role="complementary">. Mobile <768px: Radix Dialog from right.
// Keyboard: ⌘\ cycles state; Escape closes mobile drawer.
// Z-stack: edge tab z-40, inspector z-30, modals/D22 drawer z-50.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type HTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { clsx } from "clsx";
import * as Dialog from "@radix-ui/react-dialog";
import { Maximize2, Minimize2, PanelRight, Search, X } from "lucide-react";
import { useDevicePref } from "./useDevicePref";
import { useResponsiveTier } from "./useResponsiveTier";

export type RightInspectorMode = "expanded" | "rail" | "hidden";

interface RightInspectorCtx {
  mode: RightInspectorMode;
  setMode: (m: RightInspectorMode) => void;
  cycle: () => void;
  isMobile: boolean;
  mobileOpen: boolean;
  setMobileOpen: (b: boolean) => void;
  // Stores the trigger element (top-bar toggle) so close handlers can return focus.
  triggerRef: RefObject<HTMLButtonElement | null>;
  // Scroll-to-section: sections register their wrapper element by id; rail icon
  // click sets pending id which expands then scrolls in requestAnimationFrame.
  registerSection: (id: string, el: HTMLElement | null) => void;
  scrollToSection: (id: string) => void;
  // Global filter — typed at the inspector top, narrows each section in place.
  // Sections read this and filter their own items; empty string = no filter.
  query: string;
  setQuery: (q: string) => void;
  // Focus mode — at most one section may be "focused"; others collapse to
  // header-only previews. null = stacked mode (default, all sections expanded).
  focusedSectionId: string | null;
  setFocusedSectionId: (id: string | null) => void;
}

const Ctx = createContext<RightInspectorCtx | null>(null);
const COOKIE = "shell.rightInspector";

const isInspectorMode = (v: string): v is RightInspectorMode => v === "expanded" || v === "rail" || v === "hidden";

export function RightInspectorProvider({ children }: { children: ReactNode }) {
  // Device-scoped preference (cookie, device-local; default hidden at every tier — row 5.1's
  // "desktop = full sidebar+content+RightPanel" means the panel is AVAILABLE at full width, not
  // force-opened; it stays user/content-triggered exactly as today, only the breakpoint constant
  // moves onto the shared tier hook below).
  const [mode, setModeState] = useDevicePref<RightInspectorMode>(COOKIE, "hidden", isInspectorMode);
  const { isMobile } = useResponsiveTier();
  const [mobileOpen, setMobileOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map());

  // setModeState (from useDevicePref) already persists to the cookie — setMode is just the public alias.
  const setMode = setModeState;

  const cycle = useCallback(() => {
    if (isMobile) {
      setMobileOpen((o) => !o);
      return;
    }
    setModeState((prev) => (prev === "expanded" ? "rail" : prev === "rail" ? "hidden" : "expanded"));
  }, [isMobile, setModeState]);

  // ⌘\ global listener — mirrors left sidebar's ⌘B.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "\\") {
        e.preventDefault();
        cycle();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cycle]);

  const registerSection = useCallback((id: string, el: HTMLElement | null) => {
    if (el) sectionRefs.current.set(id, el);
    else sectionRefs.current.delete(id);
  }, []);

  const scrollToSection = useCallback((id: string) => {
    // If hidden/rail, expand first; scroll on next frame so layout settles. (setModeState persists.)
    setModeState((prev) => (prev === "expanded" ? prev : "expanded"));
    requestAnimationFrame(() => {
      const el = sectionRefs.current.get(id);
      el?.scrollIntoView({ block: "start", behavior: "smooth" });
    });
  }, []);

  const [query, setQuery] = useState("");
  const [focusedSectionId, setFocusedSectionId] = useState<string | null>(null);

  const value = useMemo<RightInspectorCtx>(
    () => ({
      mode,
      setMode,
      cycle,
      isMobile,
      mobileOpen,
      setMobileOpen,
      triggerRef,
      registerSection,
      scrollToSection,
      query,
      setQuery,
      focusedSectionId,
      setFocusedSectionId,
    }),
    [mode, setMode, cycle, isMobile, mobileOpen, registerSection, scrollToSection, query, focusedSectionId],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useRightInspector(): RightInspectorCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useRightInspector must be used within <RightInspectorProvider>");
  return ctx;
}

export interface RightInspectorProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
  // Optional rail-mode section icons (rendered at rail width when mode === "rail").
  railIcons?: ReactNode;
  // Footer slot — mirrors SidebarFooter (left). Renders in both expanded + rail.
  // Use this for the InspectorCollapseItem (3-state cycle button).
  footer?: ReactNode;
}

export function RightInspector({ children, railIcons, footer, className, ...props }: RightInspectorProps) {
  const { mode, isMobile, mobileOpen, setMobileOpen, triggerRef } = useRightInspector();

  if (isMobile) {
    return (
      <Dialog.Root
        open={mobileOpen}
        onOpenChange={(o) => {
          setMobileOpen(o);
          if (!o) triggerRef.current?.focus();
        }}
        modal
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-30 bg-black/40" />
          <Dialog.Content
            aria-label="Context inspector"
            className="fixed top-0 right-0 z-30 h-full w-[85vw] max-w-[85vw] bg-[var(--color-surface-elevated)] border-l border-[var(--color-border-default)] shadow-[var(--shadow-lg)] flex flex-col focus:outline-none"
          >
            <Dialog.Title className="sr-only">Context inspector</Dialog.Title>
            <InspectorFilterBar />
            <div className="flex-1 overflow-y-auto">{children}</div>
            {footer && (
              <div className="flex-shrink-0 p-2 border-t border-[var(--color-border-subtle)] space-y-1">{footer}</div>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  if (mode === "hidden") return null;

  return (
    <aside
      role="complementary"
      aria-label="Context inspector"
      className={clsx(
        // Right rail DOES get a divider (unlike the borderless left rail) — 1px --line2 hairline.
        "relative z-30 h-full border-l border-[var(--color-border-default)] bg-[var(--color-surface-elevated)] flex flex-col overflow-hidden transition-[width] duration-150",
        mode === "expanded" ? "w-[304px]" : "w-12",
        className,
      )}
      {...props}
    >
      {mode === "expanded" ? (
        <>
          <InspectorFilterBar />
          <div className="flex-1 relative overflow-hidden">
            {/* Scroll area — absolute, fills wrapper, right-padded so content doesn't hide behind rail */}
            <div className={clsx("absolute inset-0 overflow-y-auto overflow-x-hidden", railIcons && "pr-10")}>
              {children}
            </div>
            {/* Collapsed-section icon strip — pinned to the OUTER right edge (40px), --line2 left border. */}
            {railIcons && (
              <div
                aria-label="Jump to section"
                className="absolute right-0 top-0 bottom-0 w-10 border-l border-[var(--color-border-default)] py-2 flex flex-col items-center gap-1 bg-[var(--color-surface-elevated)] z-30"
              >
                {railIcons}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="flex-1 overflow-y-auto flex flex-col items-center py-2 gap-1">{railIcons}</div>
      )}
      {footer && (
        <div className="flex-shrink-0 p-2 border-t border-[var(--color-border-subtle)] space-y-1">{footer}</div>
      )}
    </aside>
  );
}

// Section wrapper for accordion layout — registers with provider for scroll-to.
export interface RightInspectorSectionProps {
  id: string;
  title: string;
  icon?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}

export function RightInspectorSection({ id, title, icon, action, children }: RightInspectorSectionProps) {
  const { registerSection, focusedSectionId, setFocusedSectionId, scrollToSection } = useRightInspector();
  const headingId = `ri-${id}-h`;

  const isFocused = focusedSectionId === id;
  const isPreview = focusedSectionId !== null && !isFocused;

  const toggleFocus = () => {
    if (isFocused) {
      setFocusedSectionId(null);
    } else {
      setFocusedSectionId(id);
      // Scroll focused section to top so its content is fully visible.
      requestAnimationFrame(() => scrollToSection(id));
    }
  };

  // In preview mode the whole header becomes a click target so the user
  // can promote the section back without having to hit the 20px Min/Max button.
  // Inner Min/Max button uses stopPropagation to avoid double-firing.
  const previewKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (isPreview && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      toggleFocus();
    }
  };

  return (
    <section
      ref={(el) => registerSection(id, el)}
      aria-labelledby={headingId}
      data-focused={isFocused || undefined}
      data-preview={isPreview || undefined}
      className="border-t border-[var(--color-border-subtle)] first:border-t-0"
    >
      <div
        onClick={isPreview ? toggleFocus : undefined}
        onKeyDown={previewKey}
        role={isPreview ? "button" : undefined}
        tabIndex={isPreview ? 0 : undefined}
        aria-expanded={isPreview ? false : undefined}
        title={isPreview ? `Click to focus ${title}` : undefined}
        className={clsx(
          "px-3.5 py-2 flex items-center gap-2 sticky top-0 z-10 bg-[var(--color-surface-elevated)]",
          isPreview && "cursor-pointer hover:bg-[var(--color-accent-subtle)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-inset",
        )}
      >
        {icon && <span className="text-[var(--color-accent)] shrink-0">{icon}</span>}
        <h3 id={headingId} className="flex-1 min-w-0 truncate text-[10px] font-medium uppercase tracking-[0.12em] text-[var(--color-text-primary)]">
          {title}
        </h3>
        {!isPreview && action}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); toggleFocus(); }}
          aria-label={isFocused ? `Exit focus on ${title}` : `Focus ${title}`}
          aria-pressed={isFocused}
          title={isFocused ? "Exit focus" : "Focus this section"}
          className={clsx(
            "shrink-0 inline-flex items-center justify-center h-5 w-5 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]",
            isFocused
              // Same pairing as Badge's accent tone, and the same 2.91:1 failure.
              ? "text-[var(--color-accent-on-subtle)] bg-[var(--color-accent-subtle)]"
              : "text-[var(--color-text-secondary)] hover:text-[var(--color-accent)] hover:bg-[var(--color-surface-elevated)]",
          )}
        >
          {isFocused ? <Minimize2 size={12} aria-hidden /> : <Maximize2 size={12} aria-hidden />}
        </button>
      </div>
      {!isPreview && <div className="px-3 pt-1 pb-3">{children}</div>}
    </section>
  );
}

// Global filter input rendered at the top of the inspector body.
// Sticky so it stays visible while scrolling sections. Sections read `query`
// from useRightInspector() and filter their items in place.
function InspectorFilterBar() {
  const { query, setQuery } = useRightInspector();
  return (
    <div className="flex-shrink-0 px-3 py-2 border-b border-[var(--color-border-subtle)] bg-[var(--color-surface-elevated)] sticky top-0 z-20 flex items-center">
      <div className="w-full flex items-center gap-1.5 h-[30px] px-2 rounded-[2px] border border-[var(--color-border-default)] bg-[var(--color-neutral-grey-15)]">
        <Search size={14} className="text-[var(--color-text-secondary)] shrink-0" aria-hidden />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter this panel…"
          aria-label="Filter inspector content"
          className="flex-1 min-w-0 bg-transparent text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-disabled)] focus:outline-none"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            aria-label="Clear filter"
            className="text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] shrink-0"
          >
            <X size={12} aria-hidden />
          </button>
        )}
      </div>
    </div>
  );
}

// Hook for sections: returns filtered items + a flag indicating an active filter.
// `getText` returns the searchable string for an item.
export function useInspectorFilter<T>(items: T[], getText: (item: T) => string): { filtered: T[]; query: string; hasQuery: boolean } {
  const { query } = useRightInspector();
  const q = query.trim().toLowerCase();
  if (!q) return { filtered: items, query: "", hasQuery: false };
  const filtered = items.filter((it) => getText(it).toLowerCase().includes(q));
  return { filtered, query: q, hasQuery: true };
}

// Edge reveal tab — co-located with the primitive (avoids cross-file dance for
// shared state). Renders only when mode === "hidden" on desktop.
export interface InspectorEdgeTabProps {
  count?: number;
  label?: string;
  icon?: ReactNode;
}

export function InspectorEdgeTab({ count = 0, label = "Show context inspector", icon }: InspectorEdgeTabProps) {
  const { mode, isMobile, setMode } = useRightInspector();
  if (isMobile) return null;
  if (mode !== "hidden") return null;

  // PanelRight (not Info ⓘ, which read as "information" not "open the panel") — a proper reveal glyph.
  const display = icon ?? (count > 0 ? <span className="text-xs font-semibold leading-none">{count}</span> : <PanelRight size={15} aria-hidden />);

  return (
    <button
      type="button"
      onClick={() => setMode("expanded")}
      aria-label={label}
      // Surface the shortcut on the COLLAPSED reveal too (mirrors the left sidebar's "(⌘B)" button),
      // so the inspector key is discoverable without opening the panel first (audit M4).
      title={`${label} (⌘\\)`}
      className={clsx(
        "fixed right-0 top-1/2 -translate-y-1/2 z-40",
        "h-14 w-7 rounded-l-md",
        "bg-[var(--color-surface-elevated)] border border-r-0 border-[var(--color-border-default)]",
        "text-[var(--color-text-secondary)] hover:text-[var(--color-accent)] hover:bg-[var(--color-surface-muted)]",
        "shadow-[var(--shadow-sm)]",
        "inline-flex items-center justify-center",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]",
      )}
    >
      {display}
    </button>
  );
}
